/**
 * Canonical Hayven color record.
 *
 * - `logoColors` are sampled from the designer's logo pack. They are for the logo artwork only:
 *   never restyle the mark with UI tokens, and never use these as UI colors.
 * - `sunrise` is the approved Hayven UI palette, and this file is its canonical record for every app
 *   and the brand-asset generator. The marketing site mirrors it as Tailwind `@theme` tokens in
 *   `apps/marketing/src/styles/global.css`; `palette.test.ts` fails if they drift.
 */

export const logoColors = {
  navy: '#250F56',
  teal: '#038F85',
  orange: '#F5921E',
  white: '#FFFFFF',
} as const;

export const sunrise = {
  ink: '#1f0f4a',
  'ink-2': '#4a4263',
  muted: '#6e6784',
  night: '#140a30',
  canvas: '#fffdfb',
  porcelain: '#faf6f3',
  line: '#ece6e2',
  violet: '#6f4bff',
  orchid: '#b15cff',
  rose: '#ff5c8a',
  tangerine: '#ff9f43',
  teal: '#0e9f92',
  /** Teal for text on white/canvas (5.0:1); `teal` itself is for fills, icons, and rings. */
  'teal-text': '#0b7d73',
  alert: '#e5484d',
  'primary-hover': '#2e1b6b',
  'violet-on-night': '#bba9ff',
  'teal-on-night': '#5ee0d2',
  'mesh-peach': '#ffd9bd',
  'mesh-pink': '#ffc4d8',
  'mesh-lilac': '#dccbff',
  'mesh-periwinkle': '#c4d3ff',
} as const;

export type LogoColorName = keyof typeof logoColors;
export type SunriseColorName = keyof typeof sunrise;

/**
 * Accent for salon-branded pages (booking, client portal) and salon-facing emails when the salon has
 * not picked a Brand Color (`Organization.brandColor` is null). A salon's own color always wins.
 */
export const DEFAULT_SALON_BRAND_COLOR = sunrise.ink;

/** Fixed Hayven color in transactional/marketing emails (header, links, buttons). White text on it = 17.1:1. */
export const CLIENT_EMAIL_BRAND_COLOR = sunrise.ink;
